/**
 * 招待ページ - 招待内容表示
 */

import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import type { PublicInvitation } from "hooks/useInvitationApi";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

type InvitationContentProps = {
  invitation: PublicInvitation;
  token: string;
};

const roleLabels: Record<string, string> = {
  owner: "オーナー",
  admin: "管理者",
  member: "メンバー",
};

export const InvitationContent: FC<InvitationContentProps> = ({
  invitation,
  token,
}) => {
  const navigate = useNavigate();
  const expiresAt = new Date(invitation.expiresAt);
  const formattedExpiry = expiresAt.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-base-200 px-4">
      <div className="card w-full max-w-lg bg-base-100 shadow-xl mx-auto">
        <div className="card-body items-center text-center">
          <div className="text-primary text-5xl mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-16 h-16"
              role="img"
              aria-label="招待メールアイコン"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
              />
            </svg>
          </div>

          <h1 className="card-title text-2xl">招待されました</h1>

          <p className="text-lg text-base-content mt-2">
            <span className="font-bold text-primary">
              {invitation.tenantName}
            </span>{" "}
            への参加招待が届いています
          </p>

          <div className="divider" />

          <div className="w-full text-left space-y-2 bg-base-200 p-4 rounded-lg">
            <div className="flex justify-between">
              <span className="text-base-content/70">メールアドレス</span>
              <span className="font-medium">{invitation.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-base-content/70">ロール</span>
              <span className="font-medium">
                {roleLabels[invitation.role] || invitation.role}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-base-content/70">有効期限</span>
              <span className="font-medium">{formattedExpiry}</span>
            </div>
          </div>

          <div className="divider" />

          <p className="text-base-content/70 text-sm mb-4">
            アカウントを作成して参加しましょう
          </p>

          <div className="card-actions w-full flex-col gap-2">
            <Button
              type="button"
              width="full"
              onClick={() =>
                navigate(
                  `/signup?email=${encodeURIComponent(invitation.email)}&token=${token}`,
                )
              }
            >
              サインアップ
            </Button>
            <p className="text-sm text-base-content/70 mt-2">
              既にアカウントをお持ちの方は <Link to="/login">ログイン</Link>{" "}
              してください
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
